import type { JsonValue } from "@sameday/core/json";
import { z } from "zod";

/**
 * What a page may tell the Operator about what it is showing. The bounds are
 * dale's, not ours: a send whose `details` break any of them is rejected
 * whole, so the browser has to be the stricter of the two.
 */
export const PAGE_DETAILS_MAX_KEYS = 20;
export const PAGE_DETAILS_MAX_KEY_CHARS = 40;
export const PAGE_DETAILS_MAX_STRING_CHARS = 200;
export const PAGE_DETAILS_MAX_LIST_ITEMS = 10;
export const PAGE_DETAILS_MAX_BYTES = 4_096;

/**
 * In a unicode-mode pattern a surrogate pair reads as the one astral
 * codepoint it spells, so this matches only an unpaired half.
 */
const loneSurrogate = /\p{Cs}/u;

/**
 * dale counts Unicode codepoints, so a surrogate pair is one character — and
 * refuses text that is not well-formed UTF-16. `JSON.stringify` would write a
 * lone surrogate as a `\ud800` escape and let it through here, where dale's
 * UTF-8 encoder rejects the whole send; keeping it out makes the two agree.
 */
const boundedString = (limit: number) =>
  z
    .string()
    .refine((value) => [...value].length <= limit)
    .refine((value) => !loneSurrogate.test(value));

const detailString = boundedString(PAGE_DETAILS_MAX_STRING_CHARS);

/**
 * A key becomes a BSON field name, which is a C string, so a NUL byte in one
 * is rejected outright. A value may hold one.
 */
const detailKey = boundedString(PAGE_DETAILS_MAX_KEY_CHARS).refine(
  (value) => !value.includes("\u0000"),
);

/**
 * `JSON.stringify` decides what dale receives, and that decides what dale
 * stores. A token with no point and no exponent is read as a Python int and
 * kept as a BSON int64, so it has to fit one; anything else is read as a
 * float, and BSON takes any finite double.
 *
 * The range has to be checked against the *token*, not the number. A double
 * near the edge prints as the shortest decimal that round-trips, which is not
 * the value itself: `-(2 ** 63)` is exactly −9223372036854775808 in binary,
 * but `JSON.stringify` writes `-9223372036854776000`, and that is what Python
 * parses — 192 below the floor, so dale refuses it. Comparing the JS number
 * would wave it through. BigInt reads the token exactly, at any width.
 */
const INT64_MIN = -(2n ** 63n);
const INT64_MAX = 2n ** 63n - 1n;

const isFloatToken = (token: string) =>
  token.includes(".") || token.includes("e");

const detailNumber = z
  .number()
  .finite()
  .refine((value) => {
    const token = JSON.stringify(value);
    if (isFloatToken(token)) return true;
    // Zod runs every check in a chain and collects the failures, so this is
    // still reached for a value `.finite()` already refused — and those
    // stringify to `null`, which is not a number BigInt can read.
    if (!/^-?\d+$/.test(token)) return false;
    const asInteger = BigInt(token);
    return asInteger >= INT64_MIN && asInteger <= INT64_MAX;
  });

const detailList = z.array(detailString).max(PAGE_DETAILS_MAX_LIST_ITEMS);

export const pageDetailsSchema = z
  .record(
    detailKey,
    z.union([detailString, detailNumber, z.boolean(), detailList]),
  )
  .refine((value) => Object.keys(value).length <= PAGE_DETAILS_MAX_KEYS);

export type PageDetails = z.infer<typeof pageDetailsSchema>;
export type PageDetailValue = PageDetails[string];

/**
 * dale drops a list longer than `PAGE_DETAILS_MAX_LIST_ITEMS`, and with it the
 * whole send's evidence — so a wide selection would tell the Operator less
 * than a narrow one. Send the first few and let the page's own count say how
 * many there really are.
 */
export const cappedList = (values: readonly string[]): string[] =>
  values.slice(0, PAGE_DETAILS_MAX_LIST_ITEMS);

/**
 * A name worth sending, or nothing. Blank and whitespace-only names are the
 * same as no name: either would reach the Operator as an empty label beside a
 * real id, reading as a row whose name nobody can see.
 *
 * The trim decides presence only — the name goes out exactly as it is stored.
 * A form that accepts `"  East  "` saves it with its spaces, so sending
 * `"East"` would hand the Operator a name that matches nothing it looks up.
 */
export const presentName = (value: string | undefined): string | undefined =>
  value?.trim() ? value : undefined;

export interface CappedSelection {
  /** Every ticked row, not just the ones named below. */
  count: number;
  ids: string[];
  labels: string[];
}

/**
 * A selection as the Operator needs it: how many rows are ticked, and the
 * first few as **aligned** ids and labels — the same rows in the same order,
 * so the i-th label names the i-th id and the Operator can say "Drain
 * cleaning" where the person would. An id alone makes it answer in numbers.
 *
 * A row with no name is labelled by its id rather than dropped, because a gap
 * would silently slide the two lists out of step. A name that is empty, or
 * only spaces, counts as no name: it would otherwise reach the Operator as a
 * blank label beside a real id, which reads as a row that has a name nobody
 * can see.
 */
export function cappedSelection<Row>(
  rows: readonly Row[],
  identify: (row: Row) => string,
  label: (row: Row) => string | undefined,
): CappedSelection {
  const head = rows.slice(0, PAGE_DETAILS_MAX_LIST_ITEMS);
  return {
    count: rows.length,
    ids: head.map(identify),
    labels: head.map((row) => presentName(label(row)) ?? identify(row)),
  };
}

/**
 * What CPython's `repr` writes for a float, which is what `json.dumps` emits.
 * It agrees with `JSON.stringify` on the digits — both pick the shortest that
 * round-trips — and differs on where the exponent starts and how it is
 * padded: `1e-7` here is `1e-07` there, and `0.00001` is `1e-05`.
 * (`pystrtod.c`: exponent form when `decpt <= -4 || decpt > 16`, written
 * `%+.02d`.)
 */
function pythonFloat(value: number): string {
  const [mantissa, exponent] = value.toExponential().split("e");
  const sign = mantissa.startsWith("-") ? "-" : "";
  const digits = mantissa.replace("-", "").replace(".", "");
  // `value` is `0.<digits> × 10 ** decpt`, so decpt is where the point goes.
  const decpt = Number(exponent) + 1;
  if (decpt <= -4 || decpt > 16) {
    const exp = decpt - 1;
    const head = digits.length > 1 ? `${digits[0]}.${digits.slice(1)}` : digits;
    const padded = String(Math.abs(exp)).padStart(2, "0");
    return `${sign}${head}e${exp < 0 ? "-" : "+"}${padded}`;
  }
  if (decpt <= 0) return `${sign}0.${"0".repeat(-decpt)}${digits}`;
  if (decpt >= digits.length) {
    return `${sign}${digits}${"0".repeat(decpt - digits.length)}.0`;
  }
  return `${sign}${digits.slice(0, decpt)}.${digits.slice(decpt)}`;
}

/**
 * The exact text dale measures: `json.dumps(details, ensure_ascii=False,
 * separators=(",", ":"))`. Strings, booleans, key order and separators come
 * out the same as `JSON.stringify`, so only the numbers need rewriting — and
 * only those dale parsed as floats, since an int survives verbatim.
 *
 * Counting `JSON.stringify`'s own output instead would be wrong in both
 * directions near the cap: `1e-7` is a byte shorter here than there, and
 * `0.00001` two bytes longer.
 */
const jsonString = (value: string) => JSON.stringify(value);

/**
 * The member union again, each branch carrying how dale would write it. The
 * branches are the same schemas `pageDetailsSchema` accepts, in the same
 * order, so a value that parsed there always encodes here — and letting the
 * parser pick the branch is what keeps this from re-deciding the type of a
 * value that has already been decided.
 */
const encodedMember = z.union([
  detailString.transform(jsonString),
  detailNumber.transform((value) => {
    const token = JSON.stringify(value);
    return isFloatToken(token) ? pythonFloat(value) : token;
  }),
  z.boolean().transform((value) => (value ? "true" : "false")),
  detailList.transform((items) => `[${items.map(jsonString).join(",")}]`),
]);

/**
 * `undefined` when a member does not encode — unreachable while the two unions
 * agree, and the honest answer if they ever drift. Falling back to the
 * browser's own encoding would hand back a byte count known to be wrong for a
 * float, which is the one thing this function exists to get right; dropping
 * the evidence costs the Operator a little context, keeping it risks a send
 * dale refuses whole.
 */
function pythonCompactJson(details: PageDetails): string | undefined {
  const members: string[] = [];
  for (const [key, value] of Object.entries(details)) {
    const encoded = encodedMember.safeParse(value);
    if (!encoded.success) return undefined;
    members.push(`${jsonString(key)}:${encoded.data}`);
  }
  return `{${members.join(",")}}`;
}

/**
 * Hold a page's evidence to dale's bounds, or drop it. The shape is already a
 * type's worth of promise; what cannot be promised at compile time is how long
 * a name is or how many rows are ticked, which is what this measures. Dropping
 * is deliberate: evidence is optional, and a page that gets it wrong must not
 * stop the person sending their message.
 *
 * `JsonValue` rather than `PageDetails` because this also reads the details
 * dale stored on a saved message, which arrive as plain JSON.
 *
 * An empty result is `undefined` rather than `{}`. dale accepts `{}` — it is
 * valid, it is simply not evidence — and a page with nothing to say should
 * leave `details` off the message rather than send an empty one.
 */
export function boundedPageDetails(value: JsonValue): PageDetails | undefined {
  const parsed = pageDetailsSchema.safeParse(value);
  if (!parsed.success || Object.keys(parsed.data).length === 0) {
    return undefined;
  }
  const text = pythonCompactJson(parsed.data);
  if (text === undefined) return undefined;
  const encoded = new TextEncoder().encode(text);
  return encoded.byteLength > PAGE_DETAILS_MAX_BYTES ? undefined : parsed.data;
}
